"use client";

import { createContext, useContext, type ReactNode } from "react";
import { translatePublic, type PublicLocale } from "../lib/public-locale";

const PublicLocaleContext = createContext<PublicLocale>("en");

export function PublicLocaleProvider({
  locale,
  children,
}: {
  locale: PublicLocale;
  children: ReactNode;
}) {
  return <PublicLocaleContext.Provider value={locale}>{children}</PublicLocaleContext.Provider>;
}

export const usePublicLocale = () => useContext(PublicLocaleContext);

export function usePublicT() {
  const locale = usePublicLocale();
  return <T,>(value: T): T => translatePublic(locale, value);
}

export function PublicLanguagePicker() {
  const locale = usePublicLocale();
  const t = usePublicT();

  return (
    <label className="public-language-picker">
      <span className="sr-only">{t("Language")}</span>
      <select
        value={locale}
        aria-label={t("Language")}
        onChange={(event) => {
          const url = new URL(window.location.href);
          url.searchParams.set("lang", event.target.value);
          window.location.assign(url);
        }}
      >
        <option lang="en" value="en">
          English
        </option>
        <option lang="fr" value="fr">
          Français
        </option>
        <option lang="ar" dir="rtl" value="ar">
          العربية
        </option>
      </select>
    </label>
  );
}

export function LegalTranslationNotice() {
  const locale = usePublicLocale();

  if (locale === "en") return null;

  const message =
    locale === "fr"
      ? "Référence juridique : cette page est publiée en anglais afin de préserver l’exactitude de la version contractuelle applicable."
      : "المرجع القانوني: تُنشر هذه الصفحة باللغة الإنجليزية للحفاظ على دقة النسخة التعاقدية المعتمدة.";

  return (
    <p className="legal-translation-notice" role="note">
      {message}
    </p>
  );
}
